import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Moon, Sparkles, Star, TrendingUp } from 'lucide-react';
import {
  InsightsLiteAspectDto,
  InsightsLiteDto,
  InsightsLiteProfileDayDto,
  InsightsLiteProfileScoreDto,
} from '../api';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { getScoreTone } from '../helpers/score-tone';
import ErrorRetryCard from './ErrorRetryCard';
import InsightsCalendar from './insights/InsightsCalendar';
import InsightsLoadingSkeleton from './insights/InsightsLoadingSkeleton';
import ProfileScoreCard from './insights/ProfileScoreCard';
import ScoreHeader from './insights/ScoreHeader';
import WeeklyStrip from './insights/WeeklyStrip';
import { PROFILE_KEYS } from './insights/profileVisuals';
import { formatLocalDate, formatShortDate, getScore } from './insights/insightsCalendarUtils';

// Como no Pro (GetTopUniqueTransitEvaluationsWithAspects), o mes destaca os
// N aspetos distintos mais fortes por contribuicao, nao o mes inteiro.
const MONTH_TOP_ASPECTS_LIMIT = 5;

const InsightsLite: React.FC = () => {
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const history = useHistory();
  const api = useAstroApiClient();
  const [insights, setInsights] = useState<InsightsLiteDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadInsights = React.useCallback(async () => {
    if (!auth.user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(false);

    try {
      const date = formatLocalDate(new Date());
      const result = await api.apiAstrologyInsightsLiteMe(date, PROFILE_KEYS.join(','));
      setInsights(result ?? null);
    } catch {
      setInsights(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [api, auth.user]);

  useEffect(() => {
    void loadInsights();
  }, [loadInsights]);

  const dailyProfiles = useMemo(() => {
    const profiles = insights?.daily?.profiles ?? [];

    return PROFILE_KEYS
      .map((profileKey) => profiles.find((profile) => profile.profileKey === profileKey))
      .filter((profile): profile is InsightsLiteProfileScoreDto => Boolean(profile));
  }, [insights?.daily?.profiles]);

  const dailyAverage = useMemo(() => {
    if (dailyProfiles.length === 0) {
      return 0;
    }

    return Math.round(
      dailyProfiles.reduce((sum, profile) => sum + (profile.percentAbsolute ?? 0), 0) / dailyProfiles.length,
    );
  }, [dailyProfiles]);

  // Sem analise textual no Lite, o mesmo transito aparece em varios perfis.
  // Agrupamos por transito identico e mostramos os perfis como multiplos badges.
  const strongestDailyAspects = useMemo(() => {
    const groups = new Map<
      string,
      { aspect: InsightsLiteAspectDto; profileKeys: string[]; strength: number }
    >();

    dailyProfiles.forEach((profile) => {
      (profile.aspects ?? []).forEach((aspect) => {
        const key = `${aspect.transit}-${aspect.aspect}-${aspect.natal}`;
        const strength = Math.abs(aspect.contribution ?? 0);
        const existing = groups.get(key);

        if (existing) {
          if (profile.profileKey && !existing.profileKeys.includes(profile.profileKey)) {
            existing.profileKeys.push(profile.profileKey);
          }
          existing.strength = Math.max(existing.strength, strength);
          return;
        }

        groups.set(key, {
          aspect,
          profileKeys: profile.profileKey ? [profile.profileKey] : [],
          strength,
        });
      });
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        profileKeys: PROFILE_KEYS.filter((profileKey) => group.profileKeys.includes(profileKey)),
      }))
      .sort((left, right) => right.strength - left.strength)
      .slice(0, 5);
  }, [dailyProfiles]);

  const weekDays = insights?.week?.calendar ?? [];
  const monthDays = insights?.month?.calendar ?? [];

  // Melhor perfil do mes (tema do mes), sem texto de analise.
  const bestMonthProfile = useMemo(() => {
    const profiles = insights?.month?.profiles ?? [];
    return [...profiles].sort(
      (left, right) => (right.avgPercentAbsolute ?? 0) - (left.avgPercentAbsolute ?? 0),
    )[0];
  }, [insights?.month?.profiles]);

  // Transitos principais do tema do mes, so titulos (sem forecast), seguindo a
  // mesma regra do Pro (GetTopUniqueTransitEvaluationsWithAspects, positivo):
  // 1) achatar (dia, aspeto); 2) dedupe por transito|natal|aspeto ficando com o
  // de maior contribuicao; 3) top-N por contribuicao; 4) reagrupar por dia e
  // ordenar por data.
  const monthTransitDays = useMemo(() => {
    const days = bestMonthProfile?.days ?? [];

    const bestPerCombo = new Map<string, { day: InsightsLiteProfileDayDto; aspect: InsightsLiteAspectDto }>();
    days.forEach((day) => {
      (day.aspects ?? []).forEach((aspect) => {
        const key = `${aspect.transit}|${aspect.natal}|${aspect.aspect}`;
        const existing = bestPerCombo.get(key);
        if (!existing || (aspect.contribution ?? 0) > (existing.aspect.contribution ?? 0)) {
          bestPerCombo.set(key, { day, aspect });
        }
      });
    });

    const top = Array.from(bestPerCombo.values())
      .sort((left, right) => (right.aspect.contribution ?? 0) - (left.aspect.contribution ?? 0))
      .slice(0, MONTH_TOP_ASPECTS_LIMIT);

    const byDate = new Map<string, { date?: Date; aspects: InsightsLiteAspectDto[] }>();
    top.forEach(({ day, aspect }) => {
      const dateKey = day.date ? day.date.toISOString() : 'unknown';
      const entry = byDate.get(dateKey) ?? { date: day.date, aspects: [] };
      entry.aspects.push(aspect);
      byDate.set(dateKey, entry);
    });

    return Array.from(byDate.values())
      .map((entry) => ({
        ...entry,
        aspects: [...entry.aspects].sort(
          (left, right) => (right.contribution ?? 0) - (left.contribution ?? 0),
        ),
      }))
      .sort((left, right) => (left.date?.getTime() ?? 0) - (right.date?.getTime() ?? 0));
  }, [bestMonthProfile]);

  const openProSubscription = () => {
    history.push('/subscription/pro', { returnTo: '/dashboard' });
  };

  const renderAspectLine = (aspect: InsightsLiteAspectDto) => (
    <span>
      {t('planet_' + aspect.transit)} {t('aspect_' + aspect.aspect)} {t('planet_' + aspect.natal)}
    </span>
  );

  if (loading) {
    return <InsightsLoadingSkeleton ariaLabel={t('insightsLiteLoading')} />;
  }

  if (loadError) {
    return (
      <ErrorRetryCard
        message={t('insightsLiteLoadError')}
        retryLabel={t('insightsLiteRetry')}
        onRetry={loadInsights}
      />
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <ScoreHeader
        title={t('insightsLiteTitle')}
        subtitle={t('insightsLiteSubtitle')}
        dateLabel={formatShortDate(insights?.date, i18n.language)}
      />

      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 border border-stone-200 bg-white p-1 shadow-sm">
          <TabsTrigger value="daily" className="text-xs data-[state=active]:bg-action-100 md:text-sm">
            {t('insightsTabToday')}
          </TabsTrigger>
          <TabsTrigger value="weekly" className="text-xs data-[state=active]:bg-action-100 md:text-sm">
            {t('insightsTabWeek')}
          </TabsTrigger>
          <TabsTrigger value="monthly" className="text-xs data-[state=active]:bg-action-100 md:text-sm">
            {t('insightsTabMonth')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
            <Card className="border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center text-slate-800">
                  <TrendingUp className="mr-2 h-5 w-5 text-emerald-600" />
                  {t('insightsLiteDailyScoresTitle')}
                </CardTitle>
                <CardDescription>{t('insightsLiteDailyScoresDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="text-4xl font-bold text-slate-900">{dailyAverage}%</div>
                  <div className="min-w-0 flex-1">
                    <Progress value={dailyAverage} className="h-3" indicatorClassName={getScoreTone(dailyAverage)} />
                    <p className="mt-1 text-sm text-slate-600">{t('insightsEnergyOverall')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-action-200 bg-gradient-to-br from-action-50 to-white shadow-sm transition-shadow hover:shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center text-slate-800">
                  <Sparkles className="mr-2 h-5 w-5 text-action-500" />
                  {t('insightsLiteProCardTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-700">{t('insightsLiteProCardDescription')}</p>
                <Button onClick={openProSubscription} className="w-full bg-action-700 text-white hover:bg-action-800">
                  {t('dashboardLiteUpgradeAction')}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {dailyProfiles.map((profile) => {
              const score = getScore(profile.percentAbsolute);
              const topAspect = [...(profile.aspects ?? [])].sort(
                (left, right) => Math.abs(right.contribution ?? 0) - Math.abs(left.contribution ?? 0),
              )[0];

              return (
                <ProfileScoreCard
                  key={profile.profileKey}
                  profileKey={profile.profileKey ?? ''}
                  score={score}
                  className="shadow-sm transition-shadow hover:shadow-md"
                  footer={topAspect ? renderAspectLine(topAspect) : t('insightsLiteNoAspects')}
                />
              );
            })}
          </div>

          <Card className="border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center text-slate-800">
                <Star className="mr-2 h-5 w-5 text-amber-500" />
                {t('insightsLiteTopAspectsTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {strongestDailyAspects.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {strongestDailyAspects.map((group, index) => (
                    <div
                      key={`${group.aspect.transit}-${group.aspect.aspect}-${group.aspect.natal}-${index}`}
                      className="rounded-lg border border-stone-200 bg-stone-50 p-3"
                    >
                      <p className="text-sm font-medium text-slate-800">{renderAspectLine(group.aspect)}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {group.profileKeys.map((profileKey) => (
                          <span
                            key={profileKey}
                            className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800"
                          >
                            {t('profile_' + profileKey)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-600">{t('insightsLiteNoAspects')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="weekly" className="space-y-4 md:space-y-6">
          <WeeklyStrip days={weekDays} />
        </TabsContent>

        <TabsContent value="monthly" className="space-y-4 md:space-y-6">
          <InsightsCalendar days={monthDays} startDate={insights?.month?.startDate} />

          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center text-slate-800">
                <Moon className="mr-2 h-6 w-6 text-purple-600" />
                {t('insightsMonthlyTheme')}{' '}
                {bestMonthProfile?.profileKey ? t('profile_' + bestMonthProfile.profileKey) : '-'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="mb-3 font-medium text-purple-900">{t('insightsMonthlyTransits')}</h4>
              {monthTransitDays.length > 0 ? (
                <div className="space-y-3">
                  {monthTransitDays.map((day) => (
                    <div key={day.date?.toISOString()} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-purple-200 px-2 py-1 text-xs font-bold text-purple-900">
                          {formatShortDate(day.date, i18n.language)}
                        </span>
                        <span className="text-xs font-medium text-purple-700">
                          {day.date?.toLocaleDateString(i18n.language, { weekday: 'long' })}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {(day.aspects ?? []).map((aspect, index) => (
                          <div
                            key={`${aspect.transit}-${aspect.aspect}-${aspect.natal}-${index}`}
                            className="flex items-start gap-3 rounded-lg border border-purple-100/50 bg-white/60 p-3 shadow-sm"
                          >
                            <Star className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" />
                            <p className="text-sm font-medium text-slate-800">{renderAspectLine(aspect)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-600">{t('insightsLiteNoAspects')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InsightsLite;
